import { Box, Button, InlineStack, Link, Select, Stack, Text, Tooltip } from '@customerio/pluma-components/react';
import {
  useBuilderActions,
  useCanAddGroup,
  useGroupNode,
  useMeta,
  useTree,
  useViewForNode,
  type LeafViewComponent,
} from '@customerio/dryad-react';
import type { GroupNode } from '@customerio/dryad';
import type { TypeOptionGroup } from '../chrome-types';
import { ADD_GROUP_OPTION_GROUP, GROUP_OPERATOR_OPTIONS, addGroupOperator } from './group-rows';
import { useGroupTreeActions } from './use-group-tree-actions';
import * as styles from './condition-group.css';

// A registered view can opt out of the group's shared row chrome by declaring
// a static `ownsRowChrome = true`: the group then renders it without the
// shared duplicate/remove buttons, and the row supplies its own affordances
// (via removeNode). For rows whose surface is a self-contained Pluma
// component (a read-only ConditionViewer sentence, say), the shared X would
// double up with the one the viewer's actions slot carries, and a duplicate of
// a display-only condition isn't editable anyway.
function viewOwnsRowChrome(view: LeafViewComponent | undefined): boolean {
  return view != null && 'ownsRowChrome' in view && view.ownsRowChrome === true;
}

type OperatorGutterProps = {
  /** The row's position, since only rows after the first carry a label. */
  index: number;
  operator: string;
};

/**
 * The fixed column the AND/OR labels sit in.
 *
 * Every row reserves it so the rows stay aligned whether or not they carry a
 * label, and the first row never does: the operator joins a row to the one
 * above it, and the first has nothing above it.
 */
function OperatorGutter({ index, operator }: OperatorGutterProps) {
  return (
    <Box as="span" className={styles.operatorGutter}>
      {index !== 0 ? (
        <Box as="span" className={styles.logicalOperatorLabel} data-testid="dryad-logical-operator">
          {operator}
        </Box>
      ) : null}
    </Box>
  );
}

type ConditionRowBodyProps = {
  rowId: string;
  /** The registered view for this row's leaf type, or undefined when none. */
  View: LeafViewComponent | undefined;
  /** Whether the view renders its own remove affordance instead of the shared one. */
  ownsChrome: boolean;
  /** False for an error node, whose raw wire no view can render. */
  hasView: boolean;
};

/** One condition: the row's own surface, and the actions that act on it. */
function ConditionRowBody({ rowId, View, ownsChrome, hasView }: ConditionRowBodyProps) {
  const meta = useMeta();
  const { duplicateNode, removeNode } = useBuilderActions();

  return (
    <>
      <Box flex={1} minW="0">
        {/* Error nodes (wire no plugin claims) and leaf types whose row hasn't
            been ported yet render read-only but stay removable, so a saved
            filter can't get stuck. */}
        {hasView && View != null ? (
          <View id={rowId} />
        ) : (
          <Text color="subtle">This condition can't be edited here yet.</Text>
        )}
      </Box>
      {ownsChrome ? null : (
        <>
          <Button
            data-testid="dryad-row-duplicate"
            aria-label="Duplicate condition"
            icon="duplicate"
            isIconOnly
            variant="tertiary"
            size="sm"
            onClick={() => duplicateNode(rowId)}
            isDisabled={meta.isDisabled}
          />
          <Button
            data-testid="dryad-row-remove"
            aria-label="Remove condition"
            icon="close"
            isIconOnly
            variant="tertiary"
            size="sm"
            onClick={() => removeNode(rowId)}
            isDisabled={meta.isDisabled}
          />
        </>
      )}
    </>
  );
}

type GroupItemRowProps = {
  rowId: string;
  rowKind: 'leaf' | 'group' | 'error';
  rowType: string | null;
  index: number;
  /** Whether the group renders the AND/OR gutter (more than one row). */
  showGutter: boolean;
  operator: string;
  /** The depth of the group holding this row; the root group's rows are at 0. */
  depth: number;
  conditionTypeOf: (value: string) => string | null;
  getLiveTree?: () => GroupNode;
  chrome: GroupChrome;
};

/**
 * One row of a group: its place in the AND/OR sequence, and either a condition
 * or a group of its own.
 */
function GroupItemRow({
  rowId,
  rowKind,
  rowType,
  index,
  showGutter,
  operator,
  depth,
  conditionTypeOf,
  getLiveTree,
  chrome,
}: GroupItemRowProps) {
  const View = useViewForNode(rowType ?? '');
  const ownsChrome = rowKind === 'leaf' && viewOwnsRowChrome(View);
  const cardShade = depth % 2 === 0 ? styles.itemCardEven : styles.itemCardOdd;

  return (
    <InlineStack data-testid={`dryad-group-item-${index}`} gap="100" alignBlock="start">
      {showGutter ? <OperatorGutter index={index} operator={operator} /> : null}
      <Box
        className={ownsChrome ? undefined : `${styles.itemCard} ${cardShade}`}
        data-testid={`dryad-group-item-kind-${rowKind}`}
        flex={1}
        minW="0"
      >
        {rowKind === 'group' ? (
          // A nested group is a row of its parent, rendering its own header,
          // rows and add menu one level down.
          <ConditionGroupBody
            groupId={rowId}
            depth={depth + 1}
            conditionTypeOf={conditionTypeOf}
            getLiveTree={getLiveTree}
            chrome={chrome}
          />
        ) : (
          <ConditionRowBody rowId={rowId} View={View} ownsChrome={ownsChrome} hasView={rowKind === 'leaf'} />
        )}
      </Box>
    </InlineStack>
  );
}

/**
 * Clears the tree back to an empty AND group. A filter-page affordance, not a
 * property of every group — a surface that wants it renders it in the slot
 * `ConditionGroup` exposes, and the rest never see it.
 */
export function ClearConditionsLink() {
  const meta = useMeta();
  const tree = useTree();
  const { setTree } = useBuilderActions();

  if (tree.children.length === 0) {
    return null;
  }

  return (
    <Link
      data-testid="dryad-clear-filters"
      onClick={() => setTree({ kind: 'group', operator: 'and', children: [] })}
      isDisabled={meta.isDisabled}
    >
      Clear filters
    </Link>
  );
}

type GroupHeaderActionsProps = {
  groupId: string;
  getLiveTree?: () => GroupNode;
};

/**
 * What a nested group's header carries that the root's does not: a copy of
 * itself, and the control that dissolves it.
 *
 * The root has neither. It has no parent to hold a copy, and dissolving it
 * would leave the tree with no group at all.
 */
function GroupHeaderActions({ groupId, getLiveTree }: GroupHeaderActionsProps) {
  const meta = useMeta();
  const { duplicateNode } = useBuilderActions();
  const { ungroup } = useGroupTreeActions(getLiveTree);

  return (
    <>
      <Box flex={1} />
      <Button
        data-testid="dryad-group-duplicate"
        aria-label="Duplicate group"
        icon="duplicate"
        isIconOnly
        variant="tertiary"
        size="sm"
        onClick={() => duplicateNode(groupId)}
        isDisabled={meta.isDisabled}
      />
      {/* A trash icon that does not delete: it lifts the group's conditions
          into the parent and removes only the group. The label says so,
          because the icon on its own reads as a delete.

          `isCustom` so the Button itself is the tooltip's trigger rather than
          being nested inside one of Tooltip's own. */}
      <Tooltip content="Ungroup conditions within" isCustom placement="top">
        {(context) => (
          <Button
            ref={context.reference.setReference}
            {...context.reference.getReferenceProps()}
            data-testid="dryad-group-ungroup"
            aria-label="Ungroup conditions within"
            icon="delete-trash"
            isIconOnly
            variant="tertiary"
            size="sm"
            onClick={() => ungroup(groupId)}
            isDisabled={meta.isDisabled}
          />
        )}
      </Tooltip>
    </>
  );
}

/**
 * The words around a group's all/any select.
 *
 * Parameterized because the operator reads as a sentence about what the group
 * holds, and that differs per surface: a filter page selects from conditions,
 * while an event trigger's group selects from one event's attributes. The
 * select itself, and everything it does, is the same either way.
 */
export type GroupSentence = {
  /** What precedes the select, when the surface's sentence opens with a word. */
  before?: string;
  /** What follows the select. */
  after: string;
};

/** The sentence a group reads as unless its surface names another. */
export const DEFAULT_GROUP_SENTENCE: GroupSentence = { after: 'of the following conditions match' };

type GroupHeaderProps = {
  groupId: string;
  operator: 'and' | 'or';
  /** What the header carries to the right of the sentence. */
  actions: React.ReactNode;
  /** The sentence around the operator. See `ConditionGroupProps`. */
  sentence: GroupSentence;
};

/** The sentence a group is read as: all or at least one of what follows. */
function GroupHeader({ groupId, operator, actions, sentence }: GroupHeaderProps) {
  const meta = useMeta();
  const { setOperator } = useBuilderActions();

  return (
    <InlineStack data-testid="dryad-group-operator" alignBlock="center" gap="100">
      {sentence.before == null ? null : <Text color="subtle">{sentence.before}</Text>}
      <Select
        ariaLabel="Group operator"
        size="sm"
        value={operator}
        options={GROUP_OPERATOR_OPTIONS}
        onChange={(value: string | null) => {
          if (value === 'and' || value === 'or') {
            setOperator(groupId, value);
          }
        }}
        isDisabled={meta.isDisabled}
      />
      <Text color="subtle">{sentence.after}</Text>
      {actions}
    </InlineStack>
  );
}

/**
 * A surface's own control for adding a condition, in place of the add menu.
 *
 * The group hands it the group to add into and nothing else: what a pick means
 * is the surface's, and the only thing it cannot work out for itself is which
 * group it is rendered under. A surface with one condition type replaces the
 * menu this way — a menu offering one entry says nothing — while a surface
 * with several keeps the menu.
 */
export type RenderAddControl = (args: { groupId: string }) => React.ReactNode;

type GroupAddMenuProps = {
  groupId: string;
  /** Whether this group may hold a group of its own, per the builder's depth. */
  canAddGroup: boolean;
  conditionTypeOf: (value: string) => string | null;
  getLiveTree?: () => GroupNode;
};

/**
 * What this group can be given: the surface's condition types, and — where
 * another level of nesting is allowed — a group of either kind.
 */
function GroupAddMenu({ groupId, canAddGroup, conditionTypeOf, getLiveTree }: GroupAddMenuProps) {
  const meta = useMeta();
  const { addNode } = useBuilderActions();
  const { addGroup } = useGroupTreeActions(getLiveTree);

  // The group entries sit after the surface's own condition types, in their
  // own section: a group is a different kind of thing from a condition, and
  // the types are what the menu is mostly for.
  const addOptions: TypeOptionGroup[] = canAddGroup ? [...meta.typeOptions, ADD_GROUP_OPTION_GROUP] : meta.typeOptions;
  const prompt = canAddGroup ? 'Add condition or group' : 'Add condition';

  const handleAdd = (value: string | null) => {
    if (value == null) {
      return;
    }
    const groupOperator = addGroupOperator(value);
    if (groupOperator != null) {
      addGroup(groupId, groupOperator);
      return;
    }
    const type = conditionTypeOf(value);
    if (type != null) {
      addNode(groupId, type);
    }
  };

  return (
    <InlineStack alignBlock="center" gap="100">
      {/* A constant empty value keeps the select on its placeholder after each pick. */}
      <Select
        data-testid="dryad-group-add"
        ariaLabel={prompt}
        size="sm"
        value=""
        placeholder={prompt}
        options={addOptions}
        onChange={handleAdd}
        isDisabled={meta.isDisabled}
      />
    </InlineStack>
  );
}

/** What a surface changes about how every one of its groups reads. */
type GroupChrome = {
  sentence: GroupSentence;
  renderAddControl?: RenderAddControl;
  withoutRootOperator?: boolean;
};

type ConditionGroupBodyProps = {
  /** The group node this renders. */
  groupId: string;
  /** 0 for the root group, one more for each level of nesting. */
  depth: number;
  /**
   * Rendered beside the root group's all/any select. Nested groups have none,
   * and a chrome with `withoutRootOperator` drops the header these sit in.
   */
  headerActions?: React.ReactNode;
  /** Narrows a picked menu value to a condition type the surface has a row for. */
  conditionTypeOf: (value: string) => string | null;
  getLiveTree?: () => GroupNode;
  chrome: GroupChrome;
};

/**
 * One group's chrome: the all/any operator, the rows with their AND/OR gutter,
 * and the add menu. Recurses through `GroupItemRow` for a row that is itself a
 * group.
 */
function ConditionGroupBody({
  groupId,
  depth,
  headerActions,
  conditionTypeOf,
  getLiveTree,
  chrome,
}: ConditionGroupBodyProps) {
  const group = useGroupNode(groupId);
  const canAddGroup = useCanAddGroup(groupId);

  if (group == null) {
    return null;
  }

  const rows = group.children;
  const isNested = depth > 0;
  // A surface whose wire has one fixed join has nothing for the select to
  // choose between, and a sentence about an operator nobody can change reads
  // as a setting the user is expected to make sense of. Nested groups keep
  // their header either way: a surface that can build one can join it.
  const withHeader = isNested || chrome.withoutRootOperator !== true;

  return (
    <Stack data-testid={`dryad-group-${depth}`} gap="100">
      {withHeader ? (
        <GroupHeader
          groupId={groupId}
          operator={group.operator}
          actions={isNested ? <GroupHeaderActions groupId={groupId} getLiveTree={getLiveTree} /> : headerActions}
          sentence={chrome.sentence}
        />
      ) : null}

      {rows.length > 0 ? (
        <Stack gap="100">
          {rows.map((row, index) => (
            <GroupItemRow
              key={row.id}
              rowId={row.id}
              rowKind={row.kind}
              rowType={row.kind === 'leaf' ? row.type : null}
              index={index}
              showGutter={rows.length > 1}
              operator={group.operator}
              depth={depth}
              conditionTypeOf={conditionTypeOf}
              getLiveTree={getLiveTree}
              chrome={chrome}
            />
          ))}
        </Stack>
      ) : null}

      {chrome.renderAddControl == null ? (
        <GroupAddMenu
          groupId={groupId}
          canAddGroup={canAddGroup}
          conditionTypeOf={conditionTypeOf}
          getLiveTree={getLiveTree}
        />
      ) : (
        chrome.renderAddControl({ groupId })
      )}
    </Stack>
  );
}

export type ConditionGroupProps = {
  /**
   * Narrows a picked menu value to a type the surface has a row for; returning
   * null ignores the pick. Keeps an unported type out of the tree.
   */
  conditionTypeOf: (value: string) => string | null;
  /**
   * Rendered beside the all/any select — where a surface puts Clear Filters or
   * anything else that belongs to the group header rather than to a row.
   */
  headerActions?: React.ReactNode;
  /**
   * The builder's own `getTree`, for a surface whose `maxDepth` allows nesting.
   *
   * Adding a group joined by `or` is an append followed by an operator flip on
   * what was appended, and only the builder can say what that is: the tree
   * every dryad-react hook reads is a subscription, so inside the click that
   * appended the group it is still the tree from before. A surface at the
   * default depth of 1 never offers a group and never needs this.
   */
  getLiveTree?: () => GroupNode;
  /**
   * The words around every group's all/any select. Defaults to the sentence a
   * filter page reads: `[All] of the following conditions match`.
   */
  sentence?: GroupSentence;
  /**
   * The surface's own add control, replacing the add menu in every group.
   *
   * For a surface whose wire has one condition type, where a menu would offer
   * a single entry. Leaving it out keeps the menu.
   */
  renderAddControl?: RenderAddControl;
  /**
   * Drops the root group's all/any header.
   *
   * For a surface whose wire has no group envelope at all: its rows are always
   * joined by one implicit AND, so a select offering a choice would offer one
   * the wire cannot store. Nested groups, where a surface has them, keep their
   * headers.
   *
   * Drops the root header whole, `headerActions` included: a surface that needs
   * actions without an operator select places them itself.
   */
  withoutRootOperator?: boolean;
};

/**
 * The root group of a surface's condition tree. Reads the builder through
 * dryad's context hooks, so it works under any surface's provider.
 *
 * How deep it nests is the builder's `maxDepth`, read per node through dryad's
 * `canAddGroup`: a surface at the default depth of 1 offers no group entries
 * and so never renders one, and a surface that allows nesting gets the group
 * entries in its add menu for as long as another level is allowed.
 */
export function ConditionGroup({
  conditionTypeOf,
  headerActions,
  getLiveTree,
  sentence = DEFAULT_GROUP_SENTENCE,
  renderAddControl,
  withoutRootOperator,
}: ConditionGroupProps) {
  const tree = useTree();

  return (
    <ConditionGroupBody
      groupId={tree.id}
      depth={0}
      headerActions={headerActions}
      conditionTypeOf={conditionTypeOf}
      getLiveTree={getLiveTree}
      chrome={{ sentence, renderAddControl, withoutRootOperator }}
    />
  );
}
